import React, { useState } from 'react';
import { Card, Col, Flex, Row, Typography } from 'antd';
import {
  BellOutlined,
  CommentOutlined,
  FileAddOutlined,
  FileTextOutlined,
  ScissorOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { VideoType } from '@monorepo/types';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../helpers/use-store';
import { useNotification } from '../../hooks/use-notification';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const Home = () => {
  const { t } = useTranslation();
  const {
    dataStore: { videoStore },
  } = useStore();
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<VideoType | null>(null);
  const notificationApi = useNotification();

  const handleCardAction = async (type: VideoType, ready) => {
    if (type === VideoType.Text) {
      navigate('/videos/form/new');
      return;
    }

    if (type === ('series' as VideoType)) {
      navigate('/series/form/new');
      return;
    }

    const videoId = (await videoStore.create({
      type,
    })) as unknown as string;

    if (videoId === '-1') {
      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('home.notifySuccess'),
      });
    } else {
      navigate(`/videos/${videoId}`);
    }
  };

  const cardConfig = [
    {
      type: VideoType.Empty,
      title: t('home.cards.empty.title'),
      ready: true,
      order: 1,
      description: t('home.cards.empty.description'),
      bestFor: t('home.cards.empty.bestFor', {
        returnObjects: true,
      }) as string[],
      icon: <FileAddOutlined />,
    },
    {
      type: 'series' as VideoType,
      title: t('home.cards.series.title'),
      ready: true,
      order: 1.5,
      description: t('home.cards.series.description'),
      bestFor: t('home.cards.series.bestFor', {
        returnObjects: true,
      }) as string[],
      icon: <ThunderboltOutlined />,
    },
    {
      type: VideoType.Text,
      title: t('home.cards.text.title'),
      ready: true,
      order: 2,
      description: t('home.cards.text.description'),
      bestFor: t('home.cards.text.bestFor', {
        returnObjects: true,
      }) as string[],
      icon: <FileTextOutlined />,
    },
    {
      type: VideoType.Captions,
      title: t('home.cards.captions.title'),
      ready: false,
      order: 3,
      description: t('home.cards.captions.description'),
      bestFor: t('home.cards.captions.bestFor', {
        returnObjects: true,
      }) as string[],
      icon: <CommentOutlined />,
    },
    {
      type: VideoType.UGC,
      title: t('home.cards.ugc.title'),
      ready: false,
      order: 4,
      description: t('home.cards.ugc.description'),
      bestFor: t('home.cards.ugc.bestFor', { returnObjects: true }) as string[],
      icon: <UserOutlined />,
    },
    {
      type: VideoType.Clip,
      title: t('home.cards.clip.title'),
      ready: false,
      order: 5,
      description: t('home.cards.clip.description'),
      bestFor: t('home.cards.clip.bestFor', {
        returnObjects: true,
      }) as string[],
      icon: <ScissorOutlined />,
    },
  ];

  // Sort by defined order
  const sortedCards = [...cardConfig].sort((a, b) => a.order - b.order);

  return (
    <div className="home">
      <Flex style={{ flexDirection: 'column', marginBottom: '15px' }}>
        <Typography.Title style={{ marginBottom: 0 }} level={4}>
          {t('nav.home')}
        </Typography.Title>
        <Text type="secondary" style={{ display: 'block', fontSize: '14px' }}>
          {t('home.title')}
        </Text>
      </Flex>
      <Row gutter={[16, 16]}>
        {sortedCards.map((card) => (
          <Col xs={24} sm={12} md={8} lg={8} key={card.title}>
            <Card
              hoverable
              onClick={() => handleCardAction(card.type, card.ready)}
              onMouseEnter={() => setHoveredCard(card.type)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#fff',
                cursor: 'pointer',
                position: 'relative',
              }}
              bodyStyle={{
                padding: '20px',
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Notify me overlay (only for soon cards on hover) */}
              {!card.ready && hoveredCard === card.type && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    zIndex: 1,
                  }}
                >
                  <BellOutlined
                    style={{
                      color: 'white',
                      fontSize: '24px',
                      marginBottom: '8px',
                    }}
                  />
                  <Text style={{ color: 'white', fontWeight: 'bold' }}>
                    {t('home.soon')}
                  </Text>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    color: card.ready ? '#1890ff' : '#d9d9d9',
                    fontSize: '24px',
                    marginRight: '12px',
                    display: 'flex',
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <Text strong style={{ fontSize: '16px', display: 'block' }}>
                    {card.title}
                    {!card.ready && (
                      <Text
                        type="warning"
                        style={{ marginLeft: '5px', fontSize: '12px' }}
                      >
                        {t('home.soon')}
                      </Text>
                    )}
                  </Text>
                </div>
              </div>

              <Text
                type="secondary"
                style={{ fontSize: '14px', marginBottom: '8px' }}
              >
                {card.description}
              </Text>

              {card.bestFor && (
                <div style={{ marginBottom: '16px' }}>
                  <Text type="secondary" strong style={{ fontSize: '13px' }}>
                    {t('home.bestFor')}
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      marginTop: '4px',
                    }}
                  >
                    {card.bestFor.map((item, index) => (
                      <Text
                        key={index}
                        type="secondary"
                        style={{
                          fontSize: '12px',
                          padding: '2px 8px',
                          background: '#f0f0f0',
                          borderRadius: '4px',
                        }}
                      >
                        {item}
                      </Text>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Home;
